import React, { useState } from "react";
import { Check, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const TIMELINE_STEPS = ["Submitted", "Reviewed", "Contractor Assigned", "Scheduled", "In Progress", "Completed"];

export function stepIndex(wo = {}) {
  const s = (wo.status || "").toLowerCase();
  if (["completed", "paid", "closed", "invoice pending", "invoiced"].some((x) => s.includes(x)) || wo.completed_at) return 5;
  if (s.includes("progress") || wo.check_in_at) return 4;
  if (wo.scheduled_date) return 3;
  if (wo.assigned_contractor_id) return 2;
  if (s && !["awaiting contractor", "new", ""].includes(s)) return 1;
  return 0;
}

const isEmergency = (u) => ["urgent", "emergency"].includes((u || "").toLowerCase());

export function EmergencyBadge({ urgency }) {
  if (!isEmergency(urgency)) return null;
  return (
    <span data-testid="emergency-badge" className="inline-flex items-center gap-1 text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300">
      <AlertTriangle className="w-3 h-3" /> Emergency
    </span>
  );
}

export function RequestNumber({ number, urgency, className = "" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(number || ""); setCopied(true); toast.success("Request number copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  if (!number) return null;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span data-testid="request-number" className="font-mono font-semibold text-slate-900">{number}</span>
      <button type="button" data-testid="copy-request-number" onClick={copy} className="text-blue-600 hover:text-blue-800" aria-label="Copy request number">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <EmergencyBadge urgency={urgency} />
    </span>
  );
}

export function StatusTimeline({ wo }) {
  const idx = stepIndex(wo);
  return (
    <ol data-testid="status-timeline" className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
      {TIMELINE_STEPS.map((label, i) => {
        const done = i <= idx;
        const current = i === idx;
        return (
          <li key={label} className="flex sm:flex-col items-center sm:flex-1 gap-2 sm:gap-1.5 relative">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 z-10 ${done ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-300 text-slate-400"} ${current ? "ring-4 ring-blue-100" : ""}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
            </div>
            <span className={`text-xs sm:text-center ${done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{label}</span>
            {i < TIMELINE_STEPS.length - 1 && <span className={`hidden sm:block absolute top-3 left-1/2 w-full h-0.5 ${i < idx ? "bg-blue-600" : "bg-slate-200"}`} />}
          </li>
        );
      })}
    </ol>
  );
}
